"use client"

import { motion } from "framer-motion"
import { Target, Lock, Lightbulb, Handshake } from "lucide-react"

const valuesData = [
  {
    title: "Precision & Accuracy",
    description:
      "We're committed to delivering high-quality solutions that achieve the highest levels of accuracy in claims processing, reducing denials to near zero.",
    icon: <Target className="w-8 h-8 text-cyan-400" />,
  },
  {
    title: "Security & Compliance",
    description:
      "Data security and regulatory compliance are at the core of our technology, ensuring full HIPAA adherence in all processes.",
    icon: <Lock className="w-8 h-8 text-cyan-400" />,
  },
  {
    title: "Innovation",
    description:
      "We continuously evolve our AI models to stay ahead of changing regulations and payer requirements in the healthcare landscape.",
    icon: <Lightbulb className="w-8 h-8 text-cyan-400" />,
  },
  {
    title: "Partnership",
    description:
      "We grow stronger by partnering with our clients to understand their unique challenges and customize solutions that drive results.",
    icon: <Handshake className="w-8 h-8 text-cyan-400" />,
  },
]

const OurValues = () => {
  const points = [
    { left: "15%", top: "60%" },
    { left: "38%", top: "20%" },
    { left: "62%", top: "60%" },
    { left: "85%", top: "20%" },
  ]

  return (
    <section
      id="our-values"
      className="relative min-h-screen bg-gradient-to-r from-[#020817] via-[#041E2F] to-[#000000] flex items-center justify-center overflow-hidden"
    >
      {/* center wrapper */}
      <div className="relative w-full max-w-full mx-auto flex flex-col justify-center">

        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 w-[600px] h-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-900/10 blur-[120px]" />
        </div>

        {/* Title */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-extrabold text-white tracking-tight"
          >
            Our Values
          </motion.h2>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:block relative w-full h-[400px] max-w-[1200px] mx-auto">

          {/* Wavy Line */}
          <div className="absolute inset-0 flex items-center justify-center">
            <svg className="w-full h-full" viewBox="0 0 1200 400" preserveAspectRatio="none">
              <defs>
                <linearGradient id="beamGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#22d3ee" stopOpacity="0" />
                  <stop offset="50%" stopColor="#22d3ee" stopOpacity="1" />
                  <stop offset="100%" stopColor="#fff" stopOpacity="1" />
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              <path
                d="M 0 200
                C 100 200, 100 240, 180 240
                S 350 80, 456 80
                S 650 240, 744 240
                S 950 80, 1020 80
                C 1100 80, 1100 200, 1200 200"
                fill="none"
                stroke="#1e3a8a"
                strokeOpacity="0.2"
                strokeWidth="2"
              />

              <motion.path
                d="M 0 200
                C 100 200, 100 240, 180 240
                S 350 80, 456 80
                S 650 240, 744 240
                S 950 80, 1020 80
                C 1100 80, 1100 200, 1200 200"
                fill="none"
                stroke="url(#beamGradient)"
                strokeWidth="3"
                strokeLinecap="round"
                filter="url(#glow)"
                initial={{ strokeDasharray: "100 1400", strokeDashoffset: 1500 }}
                animate={{ strokeDashoffset: -100 }}
                transition={{ duration: 5, ease: "linear", repeat: Infinity }}
              />
            </svg>
          </div>

          {/* Values */}
          {valuesData.map((item, index) => {
            const pos = points[index]
            const isTop = index % 2 === 1

            return (
              <motion.div
                key={index}
                className="absolute"
                style={{ left: pos.left, top: pos.top }}
                initial={{ opacity: 0, scale: 0.7 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.15 }}
              >
                <div className="relative text-center">

                  <div className="absolute -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-[0_0_15px_rgba(34,211,238,1)]" />
                  <div className="absolute -translate-x-1/2 -translate-y-1/2 w-7 h-7 border border-cyan-500/30 rounded-full" />

                  <div
                    className={`absolute left-1/2 w-[1px] h-14 bg-gradient-to-b from-cyan-400/40 to-cyan-400/0 ${
                      isTop ? "top-1 translate-y-1" : "bottom-1 -translate-y-1 rotate-180"
                    }`}
                  />

                  <div
                    className={`absolute left-1/2 -translate-x-1/2 w-64 flex flex-col items-center ${
                      isTop ? "top-16" : "bottom-16"
                    }`}
                  >
                    <div className="mb-3 bg-[#020817]/50 p-2 rounded-full backdrop-blur">
                      {item.icon}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-blue-100/80 leading-relaxed">
                      {item.description}
                    </p>
                  </div>

                </div>
              </motion.div>
            )
          })}
        </div>

        {/* Mobile */}
        <div className="md:hidden relative px-6 space-y-10 max-w-md mx-auto mt-10 pb-6">
          <div className="absolute left-6 top-0 bottom-0 w-[1px] bg-blue-900/50"></div>

          {valuesData.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="relative pl-10"
            >
              <div className="absolute left-[1.5rem] top-3 -translate-x-1/2 w-3 h-3 bg-cyan-400 rounded-full shadow-[0_0_10px_rgba(34,211,238,0.8)]" />
              <div className="mb-2">{item.icon}</div>
              <h3 className="text-xl font-bold text-white mb-1">{item.title}</h3>
              <p className="text-sm text-blue-200/70 leading-relaxed">
                {item.description}
              </p>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  )
}

export default OurValues
